import { useEffect, useState } from "react";
import AdminNav from "../../components/AdminNav";
import { getAdminSupportTickets, respondToSupportTicket } from "../../api/client";
import { listenForAdminSupport } from "../../api/socket";
import LoadingIndicator from "../../components/LoadingIndicator";

const CATEGORY_LABELS = {
  delivery_issue: "Delivery Issue",
  payment_issue: "Payment / Wallet Issue",
  account_issue: "Account Issue",
  app_bug: "App Bug",
  other: "Other",
};

const TABS = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
];

export default function AdminSupport() {
  const [tab, setTab] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [responses, setResponses] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await getAdminSupportTickets(status);
      setTickets(data);
    } catch {
      setError("Failed to load support tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);
  useEffect(() => {
    const unsubscribe = listenForAdminSupport(() => { if (tab === "open") load("open"); });
    return unsubscribe;
  }, [tab]);

  const handleRespond = async (ticketId, status) => {
    setError("");
    setBusyId(ticketId);
    try {
      await respondToSupportTicket(ticketId, status, responses[ticketId] || "");
      setResponses(prev => ({ ...prev, [ticketId]: "" }));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update ticket");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="admin-app">
      <AdminNav />
      <div className="page wide page-with-nav">

      <div className="brand-header">
        <span className="brand-logo">📨</span>
        <span className="brand-name">Support</span>
        <p className="brand-tagline">User questions, complaints, and bug reports</p>
      </div>

      <div className="role-tabs" style={{ marginBottom: "20px" }}>
        {TABS.map(t => (
          <button key={t.value} className={`role-tab ${tab === t.value ? "active" : ""}`} onClick={() => setTab(t.value)}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <LoadingIndicator label="Loading tickets..." />
      ) : tickets.length === 0 ? (
        <div className="empty-state">
          <span className="empty-state-icon">✅</span>
          <p className="empty-state-text">{tab === "open" ? "No open tickets — all clear." : "Nothing here yet."}</p>
        </div>
      ) : (
        tickets.map((t) => (
          <div key={t._id} className="rider-card" style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
              <div>
                <strong>{t.subject}</strong>
                <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                  {CATEGORY_LABELS[t.category] || t.category} · {new Date(t.createdAt).toLocaleString()}
                </p>
              </div>
              <span className={`status-badge ${t.status === "open" ? "warning" : t.status === "in_progress" ? "info" : t.status === "closed" ? "danger" : ""}`}>
                {t.status.replace(/_/g, " ").toUpperCase()}
              </span>
            </div>

            <div className="info-row">
              <span className="label">From</span>
              <span className="value">{t.user?.name} ({t.user?.phone || t.user?.email})</span>
            </div>
            {t.order && (
              <div className="info-row">
                <span className="label">Related Order</span>
                <span className="value">#{t.order._id?.slice(-6).toUpperCase()} — {t.order.status?.replace(/_/g, " ")}</span>
              </div>
            )}
            <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
              <span className="label">Message</span>
              <span className="value" style={{ marginTop: "4px" }}>{t.message}</span>
            </div>

            {t.adminResponse && (
              <div className="quote-box" style={{ marginTop: "8px" }}>
                <p style={{ fontSize: "13px" }}><strong>Previous response:</strong> {t.adminResponse}</p>
              </div>
            )}

            {!["resolved", "closed"].includes(t.status) && (
              <div style={{ marginTop: "12px" }}>
                <textarea
                  placeholder="Reply to the user (optional)"
                  value={responses[t._id] || ""}
                  onChange={e => setResponses({ ...responses, [t._id]: e.target.value })}
                  rows={2}
                  style={{ marginBottom: "8px", resize: "vertical" }}
                />
                <div className="row" style={{ gap: "8px", flexWrap: "wrap" }}>
                  <button
                    style={{ background: "#2563eb", color: "#fff", fontWeight: 600 }}
                    disabled={busyId === t._id || !responses[t._id]?.trim()}
                    onClick={() => handleRespond(t._id, null)}>
                    {busyId === t._id ? "…" : "✉️ Send Reply"}
                  </button>
                  {t.status === "open" && (
                    <button
                      className="secondary"
                      disabled={busyId === t._id}
                      onClick={() => handleRespond(t._id, "in_progress")}>
                      {busyId === t._id ? "…" : "Mark In Progress"}
                    </button>
                  )}
                  <button
                    className="success"
                    disabled={busyId === t._id}
                    onClick={() => handleRespond(t._id, "resolved")}>
                    {busyId === t._id ? "…" : "Resolve"}
                  </button>
                  <button
                    className="danger"
                    disabled={busyId === t._id}
                    onClick={() => handleRespond(t._id, "closed")}>
                    {busyId === t._id ? "…" : "Close"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
      </div>
    </div>
  );
}
